import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import {
  processingReceipts,
  type ProcessingReceiptListItem,
} from '@/api/processingReceipts';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'production.processing.receipt.manage';

export default function ProcessingReceiptsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['processing-receipts', orgId],
    queryFn: () =>
      processingReceipts.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColumnDef<ProcessingReceiptListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'processor_contractor_id',
        header: 'Переробник',
        sortValue: (r) => cName.get(r.processor_contractor_id) ?? '',
        cell: (r) => cName.get(r.processor_contractor_id) ?? '',
      },
      {
        key: 'total_cost',
        header: 'Собівартість продукції',
        width: 200,
        align: 'right',
        sortValue: (r) => (r.total_cost ? Number(r.total_cost) : 0),
        cell: (r) =>
          r.total_cost
            ? Number(r.total_cost).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'posted',
        header: 'Проведений',
        width: 140,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [cName],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати Поступлення з переробки у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Поступлення з переробки</h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
            className="primary"
            disabled={!canCreate}
            title={tooltipNew}
            style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/processing-receipts/new')}
          >
            + Нове поступлення
        </button>
      </div>
      <DataTable<ProcessingReceiptListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/processing-receipts/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
